/**
 * The way back after a dropped call (platform ADR-085).
 *
 * "Call dropped" is the outcome a rep records when the line dropped mid-conversation. The dialer
 * keeps the lead for that rep and calls them itself 15 minutes on; in those minutes the rep is
 * meant to call back by hand, from the phone, through the same campaign (ADR-047). Two things make
 * that possible without the dialer handing the rep another lead first:
 *
 * 1. The phone puts the rep on a break of its own, "Calling back" (`CALLING_BACK`), the moment
 *    the outcome is recorded — a rep on a break is never matched, and a hand dial from a break is
 *    allowed (ADR-068).
 * 2. The phone remembers who to call back (`CallBack`) and offers it: Call back now places the
 *    call; Not now ends the break. When the call back is over the ACD puts the rep back on the
 *    break (ADR-068), and the phone, seeing its own break with nobody left to call, makes them
 *    Ready again (`callingBackDone`).
 *
 * Pure decisions live here so they can be tested; the hooks in `queries.ts` hold the memory.
 */

import type { AgentStateRecord } from "./types.js";

/** The slug of the catalog's dropped-call outcome (immutable, as every slug is). */
export const DROPPED_OUTCOME = "dropped";

/** The break the phone takes on the rep's behalf while they call a dropped lead back. */
export const CALLING_BACK = { code: "calling_back", label: "Calling back" } as const;

export interface CallBack {
  leadId: string;
  /** The lead's name, or their number when the platform knows no name. */
  name: string;
  phone: string;
  campaignId: string | null;
  campaignName: string | null;
  /** When the outcome was recorded (ms since the epoch), so a stale offer is dropped. */
  since: number;
}

/** How long the offer stands: the dialer's own wait for a dropped call (ADR-085). */
export const CALL_BACK_STANDS_MS = 15 * 60_000;

/** The offer, or null when it is stale. */
export function standingCallBack(memory: CallBack | null, now: number): CallBack | null {
  if (!memory) return null;
  return now - memory.since <= CALL_BACK_STANDS_MS ? memory : null;
}

/** Whether the rep is on the phone's own break with nobody left to call back: the call back was
 *  placed and ended, or was declined, and the ACD put them back on the break (ADR-068). */
export function callingBackDone(record: AgentStateRecord | null, memory: CallBack | null): boolean {
  return record?.state === "paused" && record.code === CALLING_BACK.code && memory === null;
}

/** What the dock says above the offer. */
export function callBackTitle(cb: CallBack): string {
  return `${cb.name}'s line dropped`;
}

export function callBackDetail(cb: CallBack): string {
  const via = cb.campaignName ? ` through ${cb.campaignName}` : "";
  return `Call them back now${via}, or the dialer calls them for you in 15 minutes.`;
}
